import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CONFIG } from "@/config";
import { getUtm, postToSheet } from "@/lib/sheets";

const Field = ({ id, label, textarea, ...props }) => (
  <div className="field">
    <label htmlFor={id} className="field-label">{label}</label>
    {textarea ? (
      <textarea id={id} rows={3} className="field-input resize-none" data-testid={`enquiry-input-${id}`} {...props} />
    ) : (
      <input id={id} className="field-input" data-testid={`enquiry-input-${id}`} {...props} />
    )}
  </div>
);

const Confirmation = ({ name, onClose }) => {
  const t = CONFIG.enquiry;
  return (
    <div className="py-4 text-center" data-testid="enquiry-confirmation-message">
      <motion.svg viewBox="0 0 64 64" className="mx-auto h-16 w-16" initial="hidden" animate="visible">
        <motion.circle cx="32" cy="32" r="29" fill="none" stroke="#B6F23C" strokeWidth="2.5"
          variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1 } }} transition={{ duration: 0.6, ease: "easeOut" }} />
        <motion.path d="M20 33l8 8 16-17" fill="none" stroke="#B6F23C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1 } }} transition={{ duration: 0.4, delay: 0.5, ease: "easeOut" }} />
      </motion.svg>
      <h3 className="font-display mt-5 text-2xl font-semibold text-[#F4F5F2]">{t.confirmationTitle}</h3>
      <p className="mt-2 text-sm text-[#A3A89E]">{t.confirmationText.replace("{name}", name.split(/\s+/)[0])}</p>
      <button type="button" onClick={onClose} className="btn-outline mt-6 w-full" data-testid="btn-enquiry-close">{t.closeButtonText}</button>
    </div>
  );
};

export const EnquiryDialog = ({ option, viewer, minutesWatched, onClose }) => {
  const t = CONFIG.enquiry;
  const [values, setValues] = useState({ name: "", phone: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | failed | done

  useEffect(() => {
    if (option) {
      setValues({ name: viewer.name, phone: viewer.phone, email: viewer.email, message: "" });
      setStatus("idle");
    }
  }, [option, viewer]);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await postToSheet({
        type: "enquiry",
        option: option.title,
        name: values.name.trim(),
        phone: values.phone.trim(),
        email: values.email.trim(),
        message: values.message.trim(),
        minutesWatched,
        leadId: viewer.leadId,
        ...getUtm(),
      });
      setStatus("done");
    } catch {
      setStatus("failed");
    }
  };

  return (
    <Dialog open={!!option} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="glass max-w-md rounded-3xl border-[#282C24] p-6 sm:p-8 w-[calc(100%-1.5rem)]" data-testid="enquiry-dialog">
        {status === "done" ? (
          <Confirmation name={values.name} onClose={onClose} />
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <DialogHeader className="text-left space-y-2">
              <span className="eyebrow" data-testid="enquiry-option-label">{option?.title}</span>
              <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#F4F5F2]">{t.title}</DialogTitle>
              <DialogDescription className="text-sm text-[#A3A89E]">{t.subtitle}</DialogDescription>
            </DialogHeader>
            <Field id="name" label={CONFIG.gate.nameLabel} value={values.name} onChange={set("name")} required />
            <Field id="phone" label={CONFIG.gate.phoneLabel} value={values.phone} onChange={set("phone")} required inputMode="tel" />
            <Field id="email" label={CONFIG.gate.emailLabel} value={values.email} onChange={set("email")} required inputMode="email" />
            <Field id="notes" label={t.messageLabel} textarea value={values.message} onChange={set("message")} placeholder={t.messagePlaceholder} />
            {status === "failed" && (
              <p className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#F4F5F2]" data-testid="enquiry-error">
                We could not send your request. Please try again.
              </p>
            )}
            <button type="submit" disabled={status === "sending"} className="btn-primary w-full" data-testid="btn-submit-enquiry">
              {status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.buttonLoadingText}</> : t.buttonText}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
